import { effectiveArticlePrice, isPeriodUnit, quantityFromNights } from "@/lib/articleConstants";
import { grossAmountFromNet } from "@/lib/money";
import { normalizeRentalPeriod, type RentalPeriod } from "@/lib/rentalPeriod";
import type { Article, ArticlePrice } from "@/types/camping";

export type BillingInterval = "monthly" | "quarterly" | "semi_annual" | "annual";

const BILLING_INTERVAL_MONTHS: Record<BillingInterval, number> = {
  monthly: 1,
  quarterly: 3,
  semi_annual: 6,
  annual: 12,
};

function countNights(arrival: string, departureExclusive: string): number {
  const [ay, am, ad] = arrival.split("-").map(Number);
  const [dy, dm, dd] = departureExclusive.split("-").map(Number);
  if (
    ay === undefined ||
    am === undefined ||
    ad === undefined ||
    dy === undefined ||
    dm === undefined ||
    dd === undefined
  ) {
    return 0;
  }
  const a = Date.UTC(ay, am - 1, ad);
  const d = Date.UTC(dy, dm - 1, dd);
  return Math.max(0, Math.round((d - a) / (1000 * 60 * 60 * 24)));
}

/** Bruttobetrag für einen Abrechnungszeitraum aus Artikel und Buchungszuordnung. */
export function contractGrossFromChargeRule(
  article: Article,
  prices: ArticlePrice[],
  startDate: string,
  endDate: string,
  billingInterval: BillingInterval,
): number | null {
  const effective =
    effectiveArticlePrice(prices, startDate) ??
    (article.current_price
      ? { id: "", article_id: article.id, valid_from: startDate, amount: article.current_price, currency: "EUR" }
      : null);
  if (!effective) return null;
  const net = Number(effective.amount);
  if (!Number.isFinite(net)) return null;

  let quantity: number;
  if (isPeriodUnit(article.unit)) {
    const [ey, em, ed] = endDate.split("-").map(Number);
    if (ey === undefined || em === undefined || ed === undefined) return null;
    const endExclusive = new Date(ey, em - 1, ed + 1);
    const y = endExclusive.getFullYear();
    const m = String(endExclusive.getMonth() + 1).padStart(2, "0");
    const day = String(endExclusive.getDate()).padStart(2, "0");
    const nights = countNights(startDate, `${y}-${m}-${day}`);
    const q = quantityFromNights(nights, article.unit);
    if (q == null) return null;
    quantity = q;
  } else {
    quantity = BILLING_INTERVAL_MONTHS[billingInterval];
  }
  const subtotal = Math.round(net * quantity * 100) / 100;
  return grossAmountFromNet(subtotal, article.tax_rate_percent);
}

/** Passende Stellplatz-Buchungszuordnung für eine Platzkategorie. */
export function chargeRuleForPitchCategory(
  rules: { id: string; rule_type: string; pitch_category_id: string | null; is_active: boolean }[],
  categoryId: string | null,
): { id: string } | null {
  if (!categoryId) return null;
  return (
    rules.find(
      (rule) =>
        rule.is_active &&
        rule.rule_type === "pitch_category_night" &&
        rule.pitch_category_id === categoryId,
    ) ?? null
  );
}

export const BILLING_INTERVAL_OPTIONS: { value: BillingInterval; label: string }[] = [
  { value: "monthly", label: "Monatlich" },
  { value: "quarterly", label: "Quartalsweise" },
  { value: "semi_annual", label: "Halbjährlich" },
  { value: "annual", label: "Jährlich" },
];

export function billingIntervalLabel(interval: BillingInterval): string {
  return BILLING_INTERVAL_OPTIONS.find((o) => o.value === interval)?.label ?? interval;
}

const BILLING_INTERVAL_VALUES = new Set<string>(BILLING_INTERVAL_OPTIONS.map((option) => option.value));

const RENTAL_PERIOD_TO_BILLING_INTERVAL: Partial<Record<RentalPeriod, BillingInterval>> = {
  month: "monthly",
  quarter: "quarterly",
  semi_annual: "semi_annual",
  annual: "annual",
};

/** Vertragsintervall aus dem Abrechnungszeitraum der Stellplatzkategorie. */
export function billingIntervalFromCategory(
  category: { billing_interval?: string | null; rental_period?: string | null } | null | undefined,
): BillingInterval | null {
  if (category?.rental_period) {
    const mapped = RENTAL_PERIOD_TO_BILLING_INTERVAL[normalizeRentalPeriod(category.rental_period)];
    if (mapped) return mapped;
    if (category.rental_period === "night" || category.rental_period === "week") return null;
  }
  const value = category?.billing_interval;
  if (value && BILLING_INTERVAL_VALUES.has(value)) return value as BillingInterval;
  return null;
}

function addMonths(d: Date, months: number): Date {
  const year = d.getFullYear() + Math.floor((d.getMonth() + months) / 12);
  const month = (d.getMonth() + months) % 12;
  const lastDay = new Date(year, month + 1, 0).getDate();
  const day = Math.min(d.getDate(), lastDay);
  return new Date(year, month, day);
}

function periodStart(d: Date, interval: BillingInterval): Date {
  if (interval === "monthly") {
    return new Date(d.getFullYear(), d.getMonth(), 1);
  }
  if (interval === "quarterly") {
    const quarterMonth = Math.floor(d.getMonth() / 3) * 3;
    return new Date(d.getFullYear(), quarterMonth, 1);
  }
  if (interval === "semi_annual") {
    const halfMonth = d.getMonth() < 6 ? 0 : 6;
    return new Date(d.getFullYear(), halfMonth, 1);
  }
  return new Date(d.getFullYear(), 0, 1);
}

function periodEnd(periodStartDate: Date, interval: BillingInterval): Date {
  const months = BILLING_INTERVAL_MONTHS[interval];
  const nextStart = addMonths(periodStartDate, months);
  return new Date(nextStart.getFullYear(), nextStart.getMonth(), nextStart.getDate() - 1);
}

/** Letzter Tag des ersten Abrechnungszeitraums ab Vertragsbeginn (ISO yyyy-mm-dd). */
export function contractEndForStart(startDate: string, interval: BillingInterval): string {
  const [startYear, startMonth, startDay] = startDate.split("-").map(Number);
  if (
    startYear === undefined ||
    startMonth === undefined ||
    startDay === undefined ||
    Number.isNaN(startYear) ||
    Number.isNaN(startMonth) ||
    Number.isNaN(startDay)
  ) {
    return startDate;
  }
  const start = new Date(startYear, startMonth - 1, startDay);
  const end =
    interval === "annual" ? periodEnd(start, interval) : periodEnd(periodStart(start, interval), interval);
  const y = end.getFullYear();
  const m = String(end.getMonth() + 1).padStart(2, "0");
  const day = String(end.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function billingPeriodAmountLabel(interval: BillingInterval): string {
  switch (interval) {
    case "quarterly":
      return "Quartalspauschale (EUR, brutto)";
    case "semi_annual":
      return "Halbjahrespauschale (EUR, brutto)";
    case "annual":
      return "Jahrespauschale (EUR, brutto)";
    default:
      return "Monatspauschale (EUR, brutto)";
  }
}

export function periodInvoiceButtonLabel(interval: BillingInterval): string {
  switch (interval) {
    case "quarterly":
      return "Quartalsrechnung erzeugen";
    case "semi_annual":
      return "Halbjahresrechnung erzeugen";
    case "annual":
      return "Jahresrechnung erzeugen";
    default:
      return "Monatsrechnung erzeugen";
  }
}

export function periodInvoiceHint(interval: BillingInterval): string {
  switch (interval) {
    case "quarterly":
      return "Rechnungsentwurf für das aktuelle Quartal erzeugen.";
    case "semi_annual":
      return "Rechnungsentwurf für das aktuelle Halbjahr erzeugen.";
    case "annual":
      return "Rechnungsentwurf für das aktuelle Jahr erzeugen.";
    default:
      return "Rechnungsentwurf für den aktuellen Abrechnungsmonat erzeugen.";
  }
}
